import { dummyGroqSuggestion } from "./dummyData";

const GROQ_API_URL = "https://api.groq.com/openai/v1/chat/completions";

export interface GroqSuggestion {
  title: string;
  description: string;
  tags: string[];
  reasoning: string;
}

export const generateMetadataSuggestions = async (
  apiKey: string,
  currentTitle: string,
  currentDescription: string,
  niche: string
): Promise<GroqSuggestion> => {
  const prompt = `
    You are an expert YouTube SEO consultant. 
    Current Title: "${currentTitle}"
    Current Description: "${currentDescription}"
    Niche: "${niche}"

    Provide a viral, high-CTR title, an SEO-optimized description, and a list of 10 relevant tags.
    Explain your reasoning.
    Return ONLY JSON in the following format:
    {
      "title": "...",
      "description": "...",
      "tags": ["tag1", "tag2", ...],
      "reasoning": "..."
    }
  `;

  try {
    if (!apiKey || apiKey === "YOUR_GROQ_API_KEY") {
      return dummyGroqSuggestion;
    }

    const response = await fetch(GROQ_API_URL, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${apiKey}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        model: "mixtral-8x7b-32768",
        messages: [{ role: "user", content: prompt }],
        temperature: 0.7,
        response_format: { type: "json_object" }
      }),
    });

    const data = await response.json();
    const content = JSON.parse(data.choices[0].message.content);
    return content as GroqSuggestion;
  } catch (error) {
    console.error("Groq API Error:", error);
    throw new Error("Failed to generate AI suggestions. Please check your Groq API key.");
  }
};

export const analyzeSentiment = async (
  apiKey: string,
  comments: string[]
): Promise<{ positive: number; neutral: number; negative: number; summary: string }> => {
  const prompt = `
    Analyze the sentiment of these YouTube comments:
    ${comments.join("\n---\n")}

    Provide a count for positive, neutral, and negative. 
    Also provide a 1-sentence summary of the general audience mood.
    Return ONLY JSON:
    {
      "positive": number,
      "neutral": number,
      "negative": number,
      "summary": "..."
    }
  `;

  try {
    const response = await fetch(GROQ_API_URL, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${apiKey}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        model: "mixtral-8x7b-32768",
        messages: [{ role: "user", content: prompt }],
        temperature: 0.3,
        response_format: { type: "json_object" }
      }),
    });

    const data = await response.json();
    return JSON.parse(data.choices[0].message.content);
  } catch (error) {
    console.error("Groq Sentiment Error:", error);
    return { positive: 0, neutral: 0, negative: 0, summary: "Could not analyze sentiment at this time." };
  }
};
